/**
 * main.tsx — Application entry point.
 *
 * PERF CRITICAL: root.render() must execute as early as possible.
 * All service initialization (analytics, Sentry, storage, i18n, perf monitoring)
 * happens AFTER render via requestIdleCallback so the first paint is never blocked.
 */

import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import './index.css';

// ── Error handlers (lightweight, no imports) ──
const captureException = (err: unknown) => {
  const Sentry = (window as any).__SENTRY__;
  if (Sentry?.captureException) Sentry.captureException(err);
};

const isIntroRoute = () => window.location.pathname === '/intro';

// Track reload attempts to prevent infinite loops in FB browser
const RELOAD_KEY = '__intro_reload_count__';
const getReloadCount = (): number => {
  try { return parseInt(sessionStorage.getItem(RELOAD_KEY) || '0', 10); } catch { return 0; }
};
const incrementReloadCount = () => {
  try { sessionStorage.setItem(RELOAD_KEY, String(getReloadCount() + 1)); } catch { /* noop */ }
};
// Resets the retry cap once the app actually mounts successfully. Without this, hitting the
// 2-reload cap once (e.g. an early, unrelated network blip) permanently disables the retry
// safety net for the rest of the tab's session — including for a later, genuine failure.
const clearReloadCount = () => {
  try { sessionStorage.removeItem(RELOAD_KEY); } catch { /* noop */ }
};
// Exposed so App.tsx can reset it from the same successful-mount signal used for __bootOk.
(window as any).__clearReloadCount = clearReloadCount;

window.addEventListener('unhandledrejection', (event) => {
  const error = event.reason;
  const message = String(error?.message || '');

  // Only recover from dynamic import/chunk failures.
  // IMPORTANT: Don't reload on generic network failures (ads/analytics can fail in FB WebView).
  const isFailedImport =
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Loading chunk') ||
    message.includes('ChunkLoadError');

  if (isFailedImport) {
    event.preventDefault();
    // Prevent infinite reload loop (max 2 attempts)
    if (getReloadCount() < 2) {
      incrementReloadCount();
      setTimeout(() => window.location.reload(), 1000);
    }
    return;
  }

  if (!isIntroRoute()) captureException(event.reason);
  event.preventDefault();
});

window.addEventListener('error', (event) => {
  if (!isIntroRoute()) captureException(event.error);
});

// Same failure class as the unhandledrejection chunk-load handler above (a module/chunk
// failed to download), just surfaced via a different signal. This one previously reloaded
// unconditionally with no cap or delay — on a flaky connection (Peru mobile data through
// Facebook's in-app browser) that's an infinite reload loop with no exit, since the same
// network failure just recurs on every reload. Reuse the same capped+delayed retry so a
// user who hits this can fail out gracefully instead of being trapped.
window.addEventListener('vite:preloadError', () => {
  if (getReloadCount() < 2) {
    incrementReloadCount();
    setTimeout(() => window.location.reload(), 1000);
  }
});

// ── Mount root ──
const rootElement = document.getElementById("root");
if (!rootElement) throw new Error("Root element not found");

const root = createRoot(rootElement);

const isIntro = window.location.pathname === '/intro';

if (isIntro) {
  // ── /intro: Ultra-minimal boot — zero global services ──
  // Static HTML fallback is already in index.html, so users see content even if this fails.
  Promise.all([
    import('./pages/Intro'),
    import('react-router-dom'),
  ]).then(([{ default: Intro }, { BrowserRouter, Routes, Route }]) => {
    root.render(
      <HelmetProvider>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <Routes>
            <Route path="/intro" element={<Intro />} />
          </Routes>
        </BrowserRouter>
      </HelmetProvider>
    );
    // Tell the pre-boot watchdog (index.html) the intro mounted — no silent blank.
    try { (window as any).__bootOk?.(); } catch { /* noop */ }
    clearReloadCount();
  }).catch((err) => {
    // Import failed (e.g. FB WebView network issue) — static fallback is already visible.
    // The static HTML in index.html has a working CTA button, so users can still proceed.
    console.error('[intro] Boot failed, static fallback active:', err);
  });
} else {
  // ── Full app: render FIRST, then initialize services ──
  import('./App').then(({ default: App }) => {
    try {
      if (import.meta.env.DEV && typeof performance?.mark === 'function') {
        performance.mark('root-render-start');
      }
      root.render(
        <HelmetProvider>
          <App />
        </HelmetProvider>
      );
      if (import.meta.env.DEV && typeof performance?.mark === 'function') {
        performance.mark('root-render-end');
        performance.measure('root-render', 'root-render-start', 'root-render-end');
      }
    } catch (error) {
      console.error('Error rendering app:', error);
      captureException(error);
      try {
        (window as any).__bootBeacon?.('render-error', (error as any)?.message || String(error));
      } catch { /* noop */ }
      rootElement.innerHTML = `
        <div style="min-height:100vh;display:flex;align-items:center;justify-content:center;background:#121212;color:white;font-family:system-ui;">
          <div style="text-align:center;">
            <h1>Something went wrong</h1>
            <p>Please refresh the page to try again.</p>
            <button onclick="window.location.reload()" style="padding:10px 20px;background:#007bff;color:white;border:none;border-radius:4px;cursor:pointer;">Refresh Page</button>
          </div>
        </div>`;
    }
  });

  // ── Post-render initialization (all non-blocking) ──
  const deferToIdle = (fn: () => void, timeout = 3000) => {
    if (typeof requestIdleCallback !== 'undefined') {
      requestIdleCallback(fn, { timeout });
    } else {
      setTimeout(fn, 500);
    }
  };

  // i18n — load after first paint
  deferToIdle(() => {
    import('./lib/i18n').then(({ loadFullI18n }) => loadFullI18n());
  });

  // Analytics — after first paint, and only after first interaction OR page idle (never block render)
  const initAnalyticsOnce = () => {
    const GA_MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;
    if (GA_MEASUREMENT_ID) {
      import('./lib/analytics').then(({ initAnalytics }) => initAnalytics(GA_MEASUREMENT_ID));
    }
  };
  const runAnalyticsAfterIntent = () => {
    deferToIdle(initAnalyticsOnce, 6000);
    ['click', 'keydown', 'scroll', 'touchstart'].forEach(ev => {
      document.addEventListener(ev, function onIntent() {
        document.removeEventListener(ev, onIntent);
        initAnalyticsOnce();
      }, { once: true, passive: true });
    });
  };
  deferToIdle(runAnalyticsAfterIntent, 2000);

  // Secure storage migration
  deferToIdle(() => {
    import('./utils/secureStorageMigration').then(({ initializeSecureStorage }) => {
      initializeSecureStorage().catch(err => console.error("❌ Secure storage init failed:", err));
    });
  }, 5000);

  // Performance monitoring
  deferToIdle(() => {
    import('./utils/performanceMonitoring').then(({ initPerformanceMonitoring }) => {
      initPerformanceMonitoring();
    });
  }, 5000);

  // LCP observer
  deferToIdle(() => {
    import('./perf/lcpObserver').then(({ initLCPObserver }) => initLCPObserver());
  }, 5000);

  // Sentry — init only after user interaction so no envelopes on landing critical path (task C)
  if (import.meta.env.PROD) {
    let sentryInited = false;
    const initSentryOnce = () => {
      if (sentryInited) return;
      sentryInited = true;
      import('./sentry.client');
      ['click', 'keydown', 'scroll', 'touchstart'].forEach(ev =>
        document.removeEventListener(ev, initSentryOnce)
      );
    };
    ['click', 'keydown', 'scroll', 'touchstart'].forEach(ev =>
      document.addEventListener(ev, initSentryOnce, { once: true, passive: true })
    );
  }

  // User logout handler
  document.addEventListener('userLoggedOut', () => {
    import('./utils/clearUserData').then(({ clearUserLocalStorage }) => {
      clearUserLocalStorage();
      localStorage.setItem('proMode', 'false');
    });
  });
}
